import type { LucideIcon } from 'lucide-react';
import {
  AlertTriangle,
  Calendar,
  Download,
  FileSpreadsheet,
  FileText,
  Shirt,
  UserCheck,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { downloadCheckInReport } from '~entities/recital-manager/api/export-check-in-report';
import { downloadConflictReport } from '~entities/recital-manager/api/export-conflict-report';
import { downloadCostumeSizingCSV } from '~entities/recital-manager/api/export-costume-sizing-csv';
import { downloadPerformanceLineup } from '~entities/recital-manager/api/export-performance-lineup';
import { downloadPerformanceSchedule } from '~entities/recital-manager/api/export-performance-schedule';
import { downloadShowProgram } from '~entities/recital-manager/api/export-show-program';
import { downloadStudentList } from '~entities/recital-manager/api/export-student-list';
import { downloadVolunteerReport } from '~entities/recital-manager/api/export-volunteer-report';
import type { Recital } from '~entities/recital-manager/model/recital';

import { formatDateOnlySafe } from '~shared/helpers/datetime';
import { Button } from '~/shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~/shared/shadcn/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/shared/shadcn/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~/shared/shadcn/ui/dialog';

interface ReportsTabProps {
  recital: Recital;
}

interface BaseReport {
  id: string;
  icon: LucideIcon;
  title: string;
  description: string;
  available: boolean;
  comingSoon: boolean;
}

interface StandardReport extends BaseReport {
  csvOnly?: false;
  downloadFn: (format: 'csv' | 'pdf') => Promise<void>;
}

interface CsvOnlyReport extends BaseReport {
  csvOnly: true;
  downloadFn: () => Promise<void>;
}

type ReportDefinition = StandardReport | CsvOnlyReport;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

export function ReportsTab({ recital }: ReportsTabProps) {
  const [loadingReport, setLoadingReport] = useState<string | null>(null);
  const [showSelectionDialogOpen, setShowSelectionDialogOpen] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState<'csv' | 'pdf'>('csv');

  const parseDownloadError = async (
    error: unknown,
    reportTitle: string,
  ): Promise<string> => {
    let errorMessage = `Failed to download ${reportTitle}. Please try again.`;

    try {
      if (isRecord(error)) {
        const responseMaybe =
          'response' in error ? error.response : undefined;

        if (isRecord(responseMaybe)) {
          const responseData = responseMaybe.data;

          if (responseData instanceof Blob) {
            const text = await responseData.text();
            const trimmedText = text.trim();

            try {
              const parsed: unknown = JSON.parse(text);
              if (isRecord(parsed)) {
                const parsedMessage =
                  typeof parsed.message === 'string' &&
                  parsed.message.trim().length > 0
                    ? parsed.message
                    : typeof parsed.error === 'string' &&
                        parsed.error.trim().length > 0
                      ? parsed.error
                      : isRecord(parsed.error) &&
                          typeof parsed.error.message === 'string' &&
                          parsed.error.message.trim().length > 0
                        ? parsed.error.message
                        : undefined;
                const fallback =
                  trimmedText.length > 0 ? trimmedText : undefined;
                errorMessage = parsedMessage ?? fallback ?? errorMessage;
              } else if (trimmedText.length > 0) {
                errorMessage = trimmedText;
              }
            } catch {
              if (trimmedText.length > 0) {
                errorMessage = trimmedText;
              }
            }
          } else if (
            isRecord(responseData) &&
            typeof responseData.message === 'string' &&
            responseData.message.trim().length > 0
          ) {
            errorMessage = responseData.message;
          }
        }
      }

      // Only use generic Error message as fallback if no specific message was extracted
      if (
        errorMessage === `Failed to download ${reportTitle}. Please try again.` &&
        error instanceof Error &&
        error.message.trim().length > 0
      ) {
        errorMessage = `Failed to download ${reportTitle}. ${error.message}`;
      }
    } catch (parseError) {
      console.error('Error parsing error message:', parseError);
    }

    return errorMessage;
  };

  const downloadReport = async (
    report: ReportDefinition,
    format: 'csv' | 'pdf',
    options: { showToast?: boolean; manageLoading?: boolean } = {},
  ) => {
    const { showToast = true, manageLoading = true } = options;
    const key = `${report.id}-${format}`;

    if (manageLoading) {
      setLoadingReport(key);
    }

    try {
      if (report.csvOnly) {
        await report.downloadFn();
      } else {
        await report.downloadFn(format);
      }
      if (showToast) {
        toast.success(`${report.title} downloaded successfully`);
      }
    } catch (error: unknown) {
      console.error(`Failed to download ${report.title}:`, error);
      const errorMessage = await parseDownloadError(error, report.title);
      if (showToast) {
        toast.error(errorMessage);
      }
      throw new Error(errorMessage);
    } finally {
      if (manageLoading) {
        setLoadingReport(null);
      }
    }
  };

  const handleDownloadReport = async (
    report: ReportDefinition,
    format: 'csv' | 'pdf',
  ) => {
    if (report.csvOnly && format !== 'csv') {
      return;
    }
    await downloadReport(report, report.csvOnly ? 'csv' : format);
  };

  const handleShowSelection = async (showId: string) => {
    setShowSelectionDialogOpen(false);
    setLoadingReport(`performance-lineup-${selectedFormat}`);

    try {
      await downloadPerformanceLineup(showId, selectedFormat);
    } catch (error: unknown) {
      console.error('Failed to download Performance Lineup:', error);
      const errorMessage = await parseDownloadError(error, 'Performance Lineup');
      toast.error(errorMessage);
    } finally {
      setLoadingReport(null);
    }
  };

  const reports: Array<ReportDefinition> = [
    {
      id: 'show-program',
      icon: FileText,
      title: 'Show Program',
      description: 'Complete program with all acts and performances in order',
      available: true,
      comingSoon: false,
      downloadFn: async (format) => {
        await downloadShowProgram(recital._id.toString(), format);
      },
    },
    {
      id: 'performance-schedule',
      icon: Calendar,
      title: 'Performance Schedule',
      description: 'Timeline view with durations and timing',
      available: true,
      comingSoon: false,
      downloadFn: async (format) => {
        await downloadPerformanceSchedule(recital._id.toString(), format);
      },
    },
    {
      id: 'performance-lineup',
      icon: FileText,
      title: 'Performance Lineup',
      description: 'All performances with class assignments and student counts',
      available: true,
      comingSoon: false,
      downloadFn: async (format) => {
        if (!recital.shows?.length) {
          throw new Error(
            'Please create at least one show before generating the Performance Lineup report.',
          );
        }

        // If there's only one show, download directly
        if (recital.shows.length === 1) {
          const show = recital.shows[0];
          if (!show?._id) {
            throw new Error(
              'Show data is incomplete. Please refresh and try again.',
            );
          }
          await downloadPerformanceLineup(show._id.toString(), format);
        } else {
          // Multiple shows - open selection dialog
          setSelectedFormat(format);
          setShowSelectionDialogOpen(true);
          // Don't proceed with download here, wait for user selection
          return;
        }
      },
    },
    {
      id: 'costume-list',
      icon: Shirt,
      title: 'Costume List',
      description: 'All costumes needed for each performance',
      available: true,
      comingSoon: false,
      csvOnly: true,
      downloadFn: async () => {
        await downloadCostumeSizingCSV(recital._id.toString());
      },
    },
    {
      id: 'student-list',
      icon: Users,
      title: 'Student List',
      description: 'All students and their assigned performances',
      available: true,
      comingSoon: false,
      downloadFn: async (format) => {
        await downloadStudentList(recital._id.toString(), format);
      },
    },
    {
      id: 'conflict-report',
      icon: AlertTriangle,
      title: 'Conflict Report',
      description: 'Identify students in back-to-back performances',
      available: true,
      comingSoon: false,
      downloadFn: async (format) => {
        await downloadConflictReport(recital._id.toString(), format);
      },
    },
    {
      id: 'volunteer-report',
      icon: UserCheck,
      title: 'Volunteer Report',
      description: 'All volunteers with assignments and contact info',
      available: true,
      comingSoon: false,
      downloadFn: async (format) => {
        await downloadVolunteerReport(recital._id.toString(), format);
      },
    },
    {
      id: 'check-in-report',
      icon: UserCheck,
      title: 'Check-In Report',
      description: 'Student and volunteer check-in history',
      available: true,
      comingSoon: false,
      downloadFn: async (format) => {
        await downloadCheckInReport(recital._id.toString(), format);
      },
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Reports Center</h2>
          <p className="text-muted-foreground">
            Generate and download reports for {recital.name}
          </p>
        </div>
        {/* <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              disabled={isDownloadingAll || availableReports.length === 0}
            >
              <Download className="mr-2 size-4" />
              {isDownloadingAll ? 'Downloading...' : 'Download All Reports'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => handleDownloadAllReports('csv')}
              disabled={
                isDownloadingAll || availableReports.length === 0
              }
            >
              <FileSpreadsheet className="mr-2 size-4" />
              Download all as CSV
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => handleDownloadAllReports('pdf')}
              disabled={
                isDownloadingAll ||
                availableReports.length === 0 ||
                hasCsvOnlyReports
              }
              title={
                hasCsvOnlyReports
                  ? 'One or more reports only support CSV downloads.'
                  : undefined
              }
            >
              <FileText className="mr-2 size-4" />
              Download all as PDF
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu> */}
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {reports.map((report) => {
          const Icon = report.icon;
          const isLoading =
            loadingReport?.startsWith(report.id) ?? false;

          return (
            <Card key={report.id} className="relative">
              {report.comingSoon && (
                <div className="absolute right-3 top-3">
                  <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
                    Coming Soon
                  </span>
                </div>
              )}

              <CardHeader>
                <div className="flex items-start gap-3">
                  <div className="rounded-lg bg-primary/10 p-2">
                    <Icon className="size-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-lg">{report.title}</CardTitle>
                    <CardDescription className="mt-1.5">
                      {report.description}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>

              <CardContent>
                {!report.available || report.comingSoon ? (
                  <Button disabled className="w-full" variant="outline">
                    Not Available
                  </Button>
                ) : report.csvOnly ? (
                  <Button
                    className="w-full"
                    variant="outline"
                    onClick={() => handleDownloadReport(report, 'csv')}
                    disabled={isLoading}
                  >
                    <FileSpreadsheet className="mr-2 size-4" />
                    {isLoading ? 'Downloading...' : 'Download CSV'}
                  </Button>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        className="w-full"
                        variant="outline"
                        disabled={isLoading}
                      >
                        <Download className="mr-2 size-4" />
                        {isLoading ? 'Downloading...' : 'Generate'}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => handleDownloadReport(report, 'csv')}
                        disabled={isLoading}
                      >
                        <FileSpreadsheet className="mr-2 size-4" />
                        Download as CSV
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDownloadReport(report, 'pdf')}
                        disabled={isLoading}
                      >
                        <FileText className="mr-2 size-4" />
                        Download as PDF
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Show Selection Dialog for Performance Lineup */}
      <Dialog open={showSelectionDialogOpen} onOpenChange={setShowSelectionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Select Show</DialogTitle>
            <DialogDescription>
              Choose which show you want to generate the Performance Lineup report for.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {recital.shows?.map((show) => (
              <Button
                key={show._id}
                variant="outline"
                className="w-full justify-start"
                onClick={() => handleShowSelection(show._id.toString())}
              >
                <Calendar className='mr-2 size-4' />
                <div className='flex flex-col items-start'>
                  <span className='font-semibold'>{show.name}</span>
                  <span className='text-xs text-muted-foreground'>
                    {formatDateOnlySafe(show.showDate, 'PPP')} at {show.startTime}
                  </span>
                </div>
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
